import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { supabase } from "@vinoga/db";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

// Ported from miluliem's OrderNotifications.tsx (orders tab only —
// miluliem's "unmatched POS SKUs" tab isn't relevant on vinoga). Bell
// state syncs across devices via the /admin/notifications/seen
// endpoint so opening it on one machine calms the icon everywhere.

interface OrderRow {
  id: string;
  order_number: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_email: string | null;
  total_amount: string;
  created_at: string;
  fulfilment_type: "pickup" | "courier";
  shipping_address: Record<string, unknown> | null;
}

const POLL_INTERVAL_MS = 30_000;
const NOTIFICATION_TYPE = "orders";

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function countryCode(addr: Record<string, unknown> | null): string {
  if (!addr) return "—";
  const c = addr.country ?? addr.country_code;
  return typeof c === "string" && c ? c : "—";
}

export function OrderNotifications() {
  const navigate = useNavigate();
  const t = useT();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  // null = haven't loaded seen-state yet; epoch zero = never seen. Same
  // gating as miluliem so we don't flash unread badges before the seen
  // timestamp lands.
  const [lastSeen, setLastSeen] = useState<Date | null>(null);

  // Load this admin's last-seen timestamp once on mount.
  useEffect(() => {
    let cancelled = false;
    api<{ data: Record<string, string> }>("/api/v1/admin/notifications/seen")
      .then((json) => {
        if (cancelled) return;
        const ts = json.data?.[NOTIFICATION_TYPE];
        setLastSeen(ts ? new Date(ts) : new Date(0));
      })
      .catch(() => {
        if (!cancelled) setLastSeen(new Date(0));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchOrders = useCallback(async (seenDate: Date) => {
    const { data, error } = await supabase
      .from("orders")
      .select(
        "id, order_number, customer_first_name, customer_last_name, customer_email, total_amount, created_at, fulfilment_type, shipping_address",
      )
      .in("payment_status", ["paid", "partially_refunded", "refunded"])
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) {
      console.error("[OrderNotifications] fetch failed", error);
      return;
    }
    const rows = (data ?? []) as OrderRow[];
    setOrders(rows);
    setUnreadCount(rows.filter((o) => new Date(o.created_at) > seenDate).length);
    setLoading(false);
  }, []);

  // Poll every 30s once the seen timestamp is loaded (mirrors miluliem).
  useEffect(() => {
    if (lastSeen === null) return;
    void fetchOrders(lastSeen);
    const id = setInterval(() => fetchOrders(lastSeen), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [lastSeen, fetchOrders]);

  const markAsRead = () => {
    const now = new Date();
    setLastSeen(now);
    setUnreadCount(0);
    // Fire-and-forget — server-side persistence so the bell calms on
    // every other admin device too. Failures are tolerable (the local
    // state already updated for snappy UX).
    api(`/api/v1/admin/notifications/seen/${NOTIFICATION_TYPE}`, {
      method: "PUT",
    }).catch(() => {});
  };

  return (
    <Popover onOpenChange={(open) => open && markAsRead()}>
      <PopoverTrigger asChild>
        <button className="relative p-2 text-muted-foreground outline-none transition-colors hover:text-foreground">
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-background bg-amber-500 px-1 text-[10px] font-bold text-white">
              {unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 overflow-hidden border-border p-0 shadow-xl"
        align="end"
      >
        <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-3">
          <div className="flex flex-col">
            <h3 className="font-display text-sm font-bold text-foreground">
              {t("topnav.ordersTitle", "Latest orders")}
            </h3>
            <p className="font-body text-[10px] uppercase tracking-wider text-muted-foreground">
              {t("topnav.ordersSubtitle", "Recent activity")}
            </p>
          </div>
          {unreadCount > 0 && (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
              {unreadCount} {t("topnav.ordersNew", "new")}
            </span>
          )}
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-xs text-muted-foreground">
              {t("common.loading", "Loading…")}
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-8 text-center text-xs text-muted-foreground">
              <span className="font-body text-[11px] italic opacity-60">
                {t("topnav.ordersEmpty", "No recent orders")}
              </span>
              <button
                onClick={() => navigate("/orders")}
                className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
              >
                {t("topnav.viewAllOrders", "View all orders")}
              </button>
            </div>
          ) : (
            orders.map((order) => {
              const isNew = lastSeen
                ? new Date(order.created_at) > lastSeen
                : false;
              const customer =
                [order.customer_first_name, order.customer_last_name]
                  .filter(Boolean)
                  .join(" ") ||
                order.customer_email ||
                "—";
              return (
                <div
                  key={order.id}
                  onClick={() => navigate(`/orders/${order.id}`)}
                  className={`flex cursor-pointer flex-col gap-1 border-b border-border px-4 py-3 transition-colors last:border-0 hover:bg-muted/30 ${
                    isNew ? "bg-amber-50/20" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-body text-[13px] font-bold text-muted-foreground">
                        #{order.order_number}
                      </span>
                      {isNew && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
                    </div>
                    <span className="font-display text-[13px] font-bold text-foreground">
                      €{Number(order.total_amount).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[12px] text-muted-foreground">
                    <span className="max-w-[180px] truncate font-body">
                      {t("topnav.from", "From")}:{" "}
                      <b className="text-foreground">{customer}</b> (
                      {countryCode(order.shipping_address)})
                    </span>
                    <span className="font-body opacity-60">{fmtTime(order.created_at)}</span>
                  </div>
                  <div className="truncate font-body text-[11px] italic text-muted-foreground opacity-70">
                    {order.fulfilment_type.replace(/_/g, " ")}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div
          className="cursor-pointer border-t border-border bg-muted/30 p-3 text-center"
          onClick={() => navigate("/orders")}
        >
          <button className="text-[11px] font-bold uppercase tracking-widest text-primary transition-all hover:underline">
            {t("topnav.goToOrderList", "Go to order list")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
